import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PageHeader } from "@/components/app/Shared";
import { UserPlus, Trash2, X, Mail, KeyRound } from "lucide-react";
import { toast } from "sonner";

export default function Team() {
  const { user } = useAuth();
  const [team, setTeam] = useState([]);
  const [restaurants, setRestaurants] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({ email: "", password: "", name: "", role: "chef", restaurant_id: "" });

  const load = async () => {
    const [t, r] = await Promise.all([
      api.get("/team"),
      api.get("/restaurants"),
    ]);
    setTeam(t.data);
    setRestaurants(r.data);
  };
  useEffect(() => { load(); }, []);

  const submit = async (e) => {
    e.preventDefault();
    try {
      await api.post("/team", { ...form, restaurant_id: form.restaurant_id || null });
      toast.success("Teamlid uitgenodigd");
      setShowForm(false);
      setForm({ email: "", password: "", name: "", role: "chef", restaurant_id: "" });
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Mislukt");
    }
  };

  const del = async (uid, name) => {
    if (!confirm(`Verwijder ${name} uit het team?`)) return;
    try {
      await api.delete(`/team/${uid}`);
      toast.success("Verwijderd");
      load();
    } catch { toast.error("Mislukt"); }
  };

  return (
    <div className="p-4 sm:p-6 lg:p-8 max-w-[1200px] mx-auto" data-testid="team-page">
      <PageHeader
        overline="Beheer"
        title="Team"
        description="Voeg chefs en mede-beheerders toe aan je organisatie."
        actions={
          <button onClick={() => setShowForm(true)} className="inline-flex items-center gap-2 h-10 px-4 bg-[#5BC044] hover:bg-[#4FAA3A] text-white rounded-md text-sm font-semibold" data-testid="invite-button">
            <UserPlus size={16} /> Teamlid uitnodigen
          </button>
        }
      />

      <div className="card-flat overflow-x-auto">
        <table className="w-full text-sm min-w-[640px]">
          <thead className="bg-zinc-50 border-b border-zinc-200">
            <tr className="text-left">
              <th className="px-4 py-3 overline">Naam</th>
              <th className="px-4 py-3 overline">E-mail</th>
              <th className="px-4 py-3 overline">Rol</th>
              <th className="px-4 py-3 overline">Restaurant</th>
              <th className="px-4 py-3 overline text-right">Acties</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-zinc-100">
            {team.map((t) => {
              const r = restaurants.find((x) => x.id === t.restaurant_id);
              const isSelf = t.id === user?.id;
              return (
                <tr key={t.id} data-testid={`team-row-${t.id}`}>
                  <td className="px-4 py-3 font-medium">{t.name} {isSelf && <span className="text-xs text-zinc-400 ml-1">(jij)</span>}</td>
                  <td className="px-4 py-3 text-zinc-600 font-mono text-xs">{t.email}</td>
                  <td className="px-4 py-3">
                    {(() => {
                      const map = {
                        chef: { label: "Chef", cls: "bg-amber-100 text-amber-700" },
                        bediening: { label: "Bediening", cls: "bg-blue-100 text-blue-700" },
                        manager: { label: "Manager", cls: "bg-purple-100 text-purple-700" },
                        admin: { label: "Admin", cls: "bg-emerald-100 text-emerald-700" },
                        organization_admin: { label: "Admin", cls: "bg-emerald-100 text-emerald-700" },
                      };
                      const it = map[t.role] || { label: t.role, cls: "bg-zinc-100 text-zinc-700" };
                      return (
                        <span className={`text-[10px] font-bold uppercase tracking-wider px-2 py-1 rounded ${it.cls}`}>{it.label}</span>
                      );
                    })()}
                  </td>
                  <td className="px-4 py-3 text-zinc-700">{r?.name || "—"}</td>
                  <td className="px-4 py-3 text-right">
                    {!isSelf && (
                      <button onClick={() => del(t.id, t.name)} className="text-zinc-400 hover:text-red-600 p-1" data-testid={`remove-${t.id}`}>
                        <Trash2 size={14} />
                      </button>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {showForm && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" onClick={() => setShowForm(false)} data-testid="invite-modal">
          <form onClick={(e) => e.stopPropagation()} onSubmit={submit} className="bg-white rounded-lg max-w-md w-full p-6 shadow-xl space-y-3">
            <div className="flex items-center justify-between mb-2">
              <h3 className="text-xl font-bold tracking-tight text-[#1B2D5C]">Teamlid uitnodigen</h3>
              <button type="button" onClick={() => setShowForm(false)} className="text-zinc-400 p-1"><X size={18} /></button>
            </div>
            <div>
              <label className="overline block mb-1.5">Naam</label>
              <input required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="w-full h-10 px-3 border border-zinc-300 rounded-md text-sm" data-testid="invite-name" />
            </div>
            <div>
              <label className="overline block mb-1.5">E-mail</label>
              <input required type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="w-full h-10 px-3 border border-zinc-300 rounded-md text-sm" data-testid="invite-email" />
            </div>
            <div>
              <label className="overline block mb-1.5">Tijdelijk wachtwoord <span className="text-zinc-400 normal-case font-normal">(deel veilig met de gebruiker)</span></label>
              <input required minLength={6} type="text" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} className="w-full h-10 px-3 border border-zinc-300 rounded-md text-sm font-mono" data-testid="invite-password" />
            </div>
            <div className="grid grid-cols-2 gap-2">
              <div>
                <label className="overline block mb-1.5">Rol</label>
                <select value={form.role} onChange={(e) => setForm({ ...form, role: e.target.value })} className="w-full h-10 px-3 border border-zinc-300 rounded-md text-sm bg-white" data-testid="invite-role">
                  <option value="chef">Chef</option>
                  <option value="bediening">Bediening</option>
                  <option value="manager">Manager</option>
                  {user?.role !== "manager" && <option value="organization_admin">Admin</option>}
                </select>
              </div>
              <div>
                <label className="overline block mb-1.5">Restaurant <span className="text-zinc-400 normal-case font-normal">(optioneel)</span></label>
                <select value={form.restaurant_id} onChange={(e) => setForm({ ...form, restaurant_id: e.target.value })} className="w-full h-10 px-3 border border-zinc-300 rounded-md text-sm bg-white" data-testid="invite-restaurant">
                  <option value="">—</option>
                  {restaurants.map((r) => <option key={r.id} value={r.id}>{r.name}</option>)}
                </select>
              </div>
            </div>
            <button type="submit" className="w-full h-10 bg-[#5BC044] hover:bg-[#4FAA3A] text-white rounded-md text-sm font-semibold" data-testid="invite-submit">
              Uitnodigen
            </button>
          </form>
        </div>
      )}
    </div>
  );
}
